import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type FC,
  type ReactNode,
} from 'react';
import { NetworkPointSearch } from 'src/components/domain-specifics/network-points/network-point-search';
import { Navigation } from 'src/components/layout/app-shell/navigation';
import type { LoadedAppShellData } from 'src/components/layout/app-shell/use-app-shell-data';
import { UserControls } from 'src/components/layout/app-shell/user-controls';
import { Logo } from 'src/components/media-and-icons/logo';
import { Link } from 'src/components/navigation/link';
import { useBreakpoints } from 'src/hooks/use-breakpoints';
import { cn } from 'src/utils/cn';

type SidebarProps = {
  isExpanded: boolean;
  appShellData: LoadedAppShellData;
  toggleSidebarPreference: ReactNode;
  expandSidebarForInteraction: () => void;
};

export const sidebarTransitionDuration = 300;

export const Sidebar: FC<SidebarProps> = ({
  isExpanded,
  appShellData,
  toggleSidebarPreference: sidebarToggle,
  expandSidebarForInteraction,
}) => {
  const [isTransitioning, setIsTransitioning] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { md } = useBreakpoints();

  useEffect(() => {
    const refElement = ref.current;
    if (!refElement) return;

    const handleTransitionStart = (event: TransitionEvent) => {
      if (event.target === refElement) {
        setIsTransitioning(true);
      }
    };

    const handleTransitionEnd = (event: TransitionEvent) => {
      if (event.target === refElement) {
        setIsTransitioning(false);
      }
    };

    refElement.addEventListener('transitionstart', handleTransitionStart);
    refElement.addEventListener('transitionend', handleTransitionEnd);
    return () => {
      refElement.removeEventListener('transitionstart', handleTransitionStart);
      refElement.removeEventListener('transitionend', handleTransitionEnd);
    };
  }, []);

  return (
    <div
      ref={ref}
      className={cn(
        `
          group sticky top-0 z-1 flex h-[calc(100vh-var(--top-bar-height))]
          max-h-dvh shrink-0 flex-col gap-(--sidebar-padding)
          bg-background-sidebar-default transition-[width]
          duration-(--transition-duration) ease-in-out will-change-[width]
          data-[sidebar-expanded=false]:w-(--sidebar-width-collapsed)
          data-[sidebar-expanded=true]:w-(--sidebar-width)
          motion-reduce:transition-none
        `,
        // Top bar mode (smaller viewports)
        `pt-md`,
        // Default mode (larger viewports)
        `md:pt-(--sidebar-padding)`
      )}
      data-sidebar-expanded={isExpanded}
      data-sidebar-transitioning={isTransitioning}
      style={
        {
          '--transition-duration': `${sidebarTransitionDuration}ms`,
        } as CSSProperties
      }
      data-testid="sidebar"
    >
      {md && (
        <>
          {sidebarToggle}

          <div
            className={cn(
              `
                flex h-2xl min-h-2xl overflow-hidden
                px-[calc(var(--sidebar-padding)+var(--spacing-md))]
              `
            )}
          >
            <Link to="/" mode="default">
              <div className="flex h-2xl items-center">
                <Logo
                  mode={isExpanded ? 'default' : 'icon'}
                  className={cn(
                    'object-contain',
                    isExpanded
                      ? 'h-2xl min-h-2xl w-[110px] min-w-[110px]'
                      : 'h-[20px] min-h-[20px] w-[17px] min-w-[17px]'
                  )}
                />
              </div>
            </Link>
          </div>
        </>
      )}

      <div
        className={cn(
          `
            hidden overflow-hidden
            px-[calc(var(--sidebar-padding)+var(--spacing-md))]
          `,
          isExpanded && `block overflow-visible`
        )}
      >
        <NetworkPointSearch mode="sidebar" />
      </div>

      <Navigation
        appShellData={appShellData}
        isSidebarExpanded={isExpanded}
        isSidebarTransitioning={isTransitioning}
        expandSidebarForInteraction={expandSidebarForInteraction}
      />

      <UserControls
        appShellData={appShellData}
        isSidebarExpanded={isExpanded}
        isSidebarTransitioning={isTransitioning}
      />
    </div>
  );
};
